import { Fragment, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import { INTL_DMY_CASUAL_FORMAT, INTL_LONG_WEEKDAY } from 'constants/dateFormats'
import { convertTZ, useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { offerHasValidDepartures } from 'lib/cruises/cruiseUtils'
import { getCruiseDepartureCabinPrices } from 'lib/cruises/cruiseDeparturePricing'
import useCruiseDeparturesModel from 'hooks/Cruise/useCruiseDeparturesModel'
import { useCruiseMemberPricing } from 'hooks/Cruise/useCruiseMemberPricing'
import CruiseDepartureCabinPriceItem from 'components/Cruises/Common/Departures/CruiseDepartureCabinPriceItem'
import CruiseLoyaltyPoints from 'components/Cruises/CruiseLoyaltyPoints'
import CruiseSoldoutDepartureCard from 'components/OfferPage/Cruises/CruiseSoldoutDepartureCard'
import CruiseSoldoutStillInterested from 'components/OfferPage/Cruises/CruiseSoldoutStillInterested'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Divider from 'LuxKit/Divider'
import Heading from 'LuxKit/Typography/Heading'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import Group from 'components/utils/Group'
import type { DeparturesByDate } from 'lib/cruises/cruiseDepartures'

const DEPARTURE_ROW_MIN_WIDTH = rem(1040)

const DepartureCard = styled.div`
  min-width: 0;
  container-type: inline-size;
`

const DepartureLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas:
    'summary'
    'prices';
  gap: ${rem(16)};
  min-width: 0;

  @container (min-width: ${DEPARTURE_ROW_MIN_WIDTH}) {
    grid-template-columns: minmax(${rem(220)}, max-content) minmax(0, 1fr);
    grid-template-areas: 'summary prices';
    align-items: center;
    column-gap: ${rem(20)};
  }
`

const DepartureSummary = styled(Group)`
  grid-area: summary;
  min-width: 0;
`

const PricesGrid = styled.div<{ $columnCount: number }>`
  grid-area: prices;
  display: grid;
  column-gap: ${rem(12)};
  row-gap: ${rem(20)};
  grid-template-columns: repeat(${props => props.$columnCount}, minmax(${rem(128)}, 1fr));
  overflow-x: auto;
`

const PriceItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  min-width: 0;

  & + & {
    border-inline-start: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
    padding-inline-start: ${rem(12)};
  }
`

interface DepartureTileProps {
  offer: App.CruiseOffer
  departure: App.CruiseDepartureDetails
}

function CruiseDrawerDepartureTile({
  offer,
  departure,
}: DepartureTileProps) {
  const dateFormatter = useIntlDateFormatter()
  const { departureMemberPricingByCategory } = useCruiseMemberPricing(offer, departure)
  const prices = useMemo(
    () => getCruiseDepartureCabinPrices(offer, departure, departureMemberPricingByCategory),
    [departure, departureMemberPricingByCategory, offer],
  )
  const dates = useMemo(() => {
    const departureDate = convertTZ(new Date(departure.departureDate!), 'UTC')
    const arrivalDate = convertTZ(new Date(departure.arrivalDate!), 'UTC')

    return {
      arrivalLabel: dateFormatter(arrivalDate, INTL_DMY_CASUAL_FORMAT),
      arrivalWeekdayLabel: dateFormatter(arrivalDate, INTL_LONG_WEEKDAY),
      departureLabel: dateFormatter(departureDate, INTL_DMY_CASUAL_FORMAT),
      departureWeekdayLabel: dateFormatter(departureDate, INTL_LONG_WEEKDAY),
    }
  }, [dateFormatter, departure.arrivalDate, departure.departureDate])

  return <DepartureCard data-testid="cruise-drawer-departure-card">
    <DepartureLayout>
      <DepartureSummary direction="vertical" gap={12}>
        <Group direction="horizontal" gap={12}>
          <div>
            <Heading variant="heading4" wrap="no-wrap">{dates.departureLabel}</Heading>
            <BodyText variant="small" weight="normal" colour="light-foreground-alt">
              {dates.departureWeekdayLabel}
            </BodyText>
          </div>
          <LineArrowRightIcon />
          <div>
            <Heading variant="heading4" wrap="no-wrap">{dates.arrivalLabel}</Heading>
            <BodyText variant="small" weight="normal" colour="light-foreground-alt">
              {dates.arrivalWeekdayLabel}
            </BodyText>
          </div>
        </Group>

        <CruiseLoyaltyPoints offer={offer} calculationType="estimate" />
      </DepartureSummary>

      <PricesGrid $columnCount={Math.max(prices.length, 1)}>
        {prices.map((price) => <PriceItem key={price.category}>
          <CruiseDepartureCabinPriceItem
            offer={offer}
            price={price}
          />
        </PriceItem>)}

        {prices.length === 0 && <BodyTextBlock variant="medium" colour="light-foreground-alt">
          <FormattedMessage
            id="MS-KJ65nJRQALFaMLLgk"
            defaultMessage="No prices available"
            description="Empty state message shown when no cabin prices are available for a cruise departure"
          />
        </BodyTextBlock>}
      </PricesGrid>
    </DepartureLayout>
  </DepartureCard>
}

interface Props {
  offer: App.CruiseOffer
}

function CruiseDrawerDepartures({
  offer,
}: Props) {
  const departuresByDate = (offer.departures ?? {}) as DeparturesByDate
  const hasValidDepartures = offerHasValidDepartures(offer)
  const {
    departuresByMonth,
  } = useCruiseDeparturesModel({
    departuresByDate,
    lowestDeparturePricesByCategory: offer.lowestDeparturePricesByCategory,
  })

  return <Group direction="vertical" gap={24}>
    <Heading variant="heading3">
      <FormattedMessage
        id="A51U7aAfppFcGWSpIvSj"
        defaultMessage="Dates & Pricing"
        description="Section heading for cruise departure dates and pricing"
      />
    </Heading>

    <Group direction="vertical" gap={28}>
      {departuresByMonth.map(([dateLabel, departures]) => <Group direction="vertical" gap={12} key={dateLabel}>
        <BodyText variant="large" weight="bold">{dateLabel}</BodyText>
        {departures.map(({ departure }) => <Fragment key={departure.id}>
          <CruiseDrawerDepartureTile
            offer={offer}
            departure={departure}
          />
          <Divider kind="secondary" />
        </Fragment>)}
      </Group>)}

      {!hasValidDepartures && <CruiseSoldoutDepartureCard />}
      {!hasValidDepartures && <CruiseSoldoutStillInterested useLinkButton />}
    </Group>
  </Group>
}

export default CruiseDrawerDepartures
